import classNames from "classnames";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { HeadCellContent } from "components/DesignSystem/HeadCellContent/HeadCellContent";
import Table from "components/DesignSystem/Table/V2/Table";
import { CaretDown } from "components/icons/CaretDown";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { Fragment, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "react-table-8.10.7";
import {
  CategorisationHistoryItem,
  useGetCategorisationHistoryQuery,
} from "store/apis/categorisationHistory";
import { CategorisationTxnTable } from "./CategorisationTxnTable";
import { TableUI } from "components/design/TableUI";
import { AccordionAnimation } from "components/AccordionAnimation";
import { AnimatePresence } from "framer-motion";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";

const columnHelper = createColumnHelper<CategorisationHistoryItem>();

const COLUMN_IDS = {
  RADIO: "RADIO",
  ACCORDION_CATEGORY: "ACCORDION_CATEGORY",
  CATEGORY: "CATEGORY",
  TXN_COUNT: "TXN_COUNT",
  TXN_COUNT_BUTTON: "TXN_COUNT_BUTTON",
  LAST_USED: "LAST_USED",
} as const;

type CategorisationHistoryTableMeta = {
  expandedCategoryId: string;
  setExpandedCategoryId: React.Dispatch<React.SetStateAction<string>>;
  selectedCategoryId?: string;
  onCategorySelect?: (categoryId: string) => void;
  onRadioSelect?: (categoryId: string) => void;
};

const columns = [
  columnHelper.display({
    id: COLUMN_IDS.RADIO,
    size: 5,
    header: "",
    cell: ({ row, table }) => {
      const { selectedCategoryId, onRadioSelect } = table.options
        .meta as CategorisationHistoryTableMeta;

      return (
        <div
          className="t-flex t-items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="radio"
            name="category"
            checked={selectedCategoryId === row.original.uuid}
            onChange={() => onRadioSelect?.(row.original.uuid)}
            className="t-size-3.5 t-accent-purple-55"
          />
        </div>
      );
    },
  }),

  columnHelper.accessor("category_name", {
    id: COLUMN_IDS.ACCORDION_CATEGORY,
    size: 5,
    enableSorting: false,
    header: "",
    cell: (info) => {
      const { expandedCategoryId } = info.table.options
        .meta as CategorisationHistoryTableMeta;
      const category = info.row.original;
      const isExpanded = expandedCategoryId === category.uuid;

      return (
        <div className="t-flex t-items-center t-gap-2">
          <span
            className={classNames(
              "t-text-neutral t-transform t-transition t-duration-300 t-ease-in-out",
              {
                "-t-rotate-0": isExpanded,
                "-t-rotate-90": !isExpanded,
              }
            )}
          >
            <CaretDown />
          </span>
        </div>
      );
    },
  }),

  columnHelper.accessor("category_name", {
    id: COLUMN_IDS.CATEGORY,
    size: 40,
    enableSorting: false,
    header: "Category",
    cell: (info) => {
      const { identifier, category_name } = info.row.original;

      return (
        <span className="t-text-body-sm t-text-text-60">
          {identifier} - {category_name}
        </span>
      );
    },
  }),

  columnHelper.accessor("txn_count", {
    id: COLUMN_IDS.TXN_COUNT,
    size: 30,
    header: (header) => (
      <HeadCellContent
        title="Number of Transactions"
        align="center"
        sort={{
          nextSortOrder: header.column.getNextSortingOrder(),
          isSorted: header.column.getIsSorted(),
          sortingHandler: header.column.getToggleSortingHandler(),
        }}
      />
    ),
    cell: (info) => (
      <span className="t-flex t-justify-center t-text-text-60">
        {info.getValue()}
      </span>
    ),
  }),

  columnHelper.accessor("txn_count", {
    id: COLUMN_IDS.TXN_COUNT_BUTTON,
    size: 30,
    header: (header) => (
      <HeadCellContent
        title="Number of Transactions"
        align="center"
        sort={{
          nextSortOrder: header.column.getNextSortingOrder(),
          isSorted: header.column.getIsSorted(),
          sortingHandler: header.column.getToggleSortingHandler(),
        }}
      />
    ),
    cell: (info) => {
      const { setExpandedCategoryId, expandedCategoryId } = info.table.options
        .meta as CategorisationHistoryTableMeta;
      const category = info.row.original;

      return (
        <div className="t-flex t-justify-center t-w-full">
          <span
            className={classNames("t-cursor-pointer", {
              "t-text-purple t-underline": expandedCategoryId === category.uuid,
              "t-text-body-sm t-text-text-60 hover:t-text-purple hover:t-underline":
                expandedCategoryId !== category.uuid,
            })}
            onClick={(e) => {
              e.stopPropagation();
              setExpandedCategoryId((prev) =>
                prev === category.uuid ? "" : category.uuid
              );
            }}
          >
            {info.getValue()}
          </span>
        </div>
      );
    },
  }),

  columnHelper.accessor("last_used", {
    id: COLUMN_IDS.LAST_USED,
    size: 20,
    header: (header) => (
      <HeadCellContent
        title="Last used"
        align="left"
        sort={{
          nextSortOrder: header.column.getNextSortingOrder(),
          isSorted: header.column.getIsSorted(),
          sortingHandler: header.column.getToggleSortingHandler(),
        }}
      />
    ),
    cell: (info) => (
      <span className="t-text-body-sm t-text-text-60">
        {dayjs(info.getValue()).format(DISPLAY_DATE)}
      </span>
    ),
  }),
];

export const CATEGORISATION_HISTORY_VARIANT = {
  ACCORDION: "accordion",
  RADIO: "radio",
} as const;

type CategorisationHistoryVariant =
  (typeof CATEGORISATION_HISTORY_VARIANT)[keyof typeof CATEGORISATION_HISTORY_VARIANT];

type CategorisationHistoryTableProps = {
  merchantId: string;
  startDate?: string;
  endDate?: string;
  variant?: CategorisationHistoryVariant;
  selectedCategoryId?: string;
  onCategorySelect?: (categoryId: string) => void;
  onTransactionClick?: (transactionId: string) => void;
};

export const CategorisationHistoryTable = ({
  merchantId,
  startDate,
  endDate,
  variant = CATEGORISATION_HISTORY_VARIANT.ACCORDION,
  onCategorySelect,
  onTransactionClick,
}: CategorisationHistoryTableProps) => {
  const { uuid: groupId } = useCurrentGroup();
  const entityId = useCurrentEntityId();
  const [rowSelection, setRowSelection] = useState({});
  const [expandedCategoryId, setExpandedCategoryId] = useState<string>("");
  const [sorting, setSorting] = useState<SortingState>([
    { id: COLUMN_IDS.TXN_COUNT, desc: true },
  ]);

  const isAccordionVariant =
    variant === CATEGORISATION_HISTORY_VARIANT.ACCORDION;
  const isRadioVariant = variant === CATEGORISATION_HISTORY_VARIANT.RADIO;

  const { data, isLoading, isSuccess } = useGetCategorisationHistoryQuery(
    {
      merchantId,
      startDate,
      endDate,
      groupId: groupId!,
      entityId: entityId!,
    },
    {
      skip: !merchantId || !entityId,
    }
  );

  const categories = data || [];
  const isEmpty = categories.length === 0;

  const selectedCategoryId = Object.keys(rowSelection)[0];

  const columnVisibility = {
    [COLUMN_IDS.RADIO]: isRadioVariant,
    [COLUMN_IDS.ACCORDION_CATEGORY]: isAccordionVariant,
    [COLUMN_IDS.CATEGORY]: true,
    [COLUMN_IDS.TXN_COUNT]: isAccordionVariant,
    [COLUMN_IDS.TXN_COUNT_BUTTON]: isRadioVariant,
    [COLUMN_IDS.LAST_USED]: true,
  };

  const table = useReactTable({
    data: categories,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
      columnVisibility,
      rowSelection,
    },
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
    enableRowSelection: true,
    meta: {
      expandedCategoryId,
      setExpandedCategoryId,
      selectedCategoryId,
      onCategorySelect,
      onRadioSelect: (categoryId: string) => {
        setRowSelection({ [categoryId]: true });
        onCategorySelect?.(categoryId);
      },
    } as CategorisationHistoryTableMeta,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.uuid,
  });

  const handleRowClick = (categoryId: string) => {
    if (isAccordionVariant) {
      setExpandedCategoryId((prev) => (prev === categoryId ? "" : categoryId));
      return;
    }

    if (isRadioVariant) {
      setRowSelection({ [categoryId]: true });
      onCategorySelect?.(categoryId);
    }
  };

  return (
    <Async.Root isLoading={isLoading} isEmpty={isEmpty} isSuccess={isSuccess}>
      <Async.Empty>
        <div className="t-text-text-60 t-text-body-sm t-text-center t-w-full t-p-8">
          No categorization history found
        </div>
      </Async.Empty>
      <Async.Success>
        <TableUI
          table={table}
          isHeaderCellUpperCase={false}
          header={({ headerGroup }) => (
            <Table.HeadRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.HeadCell
                  key={header.id}
                  isUpperCase={false}
                  className="t-group"
                  style={{ width: `${header.getSize()}%` }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </Table.HeadCell>
              ))}
            </Table.HeadRow>
          )}
        >
          {({ row }) => {
            const isExpanded = expandedCategoryId === row.original.uuid;

            return (
              <Fragment key={row.id}>
                <Table.Row
                  className="t-cursor-pointer"
                  onRowClick={() => handleRowClick(row.original.uuid)}
                  isRowSelected={row.getIsSelected()}
                >
                  {row.getVisibleCells().map((cell) => (
                    <Table.Cell
                      key={cell.id}
                      style={{ width: `${cell.column.getSize()}%` }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
                <AnimatePresence>
                  {isExpanded && (
                    <Table.Row key={`${row.id}-expanded`}>
                      <Table.Cell
                        colSpan={table.getVisibleLeafColumns().length}
                        className="!t-p-0 t-bg-surface-lighter-grey"
                      >
                        <AccordionAnimation>
                          <CategorisationTxnTable
                            merchantId={merchantId}
                            categoryId={row.original.uuid}
                            onRowClick={onTransactionClick}
                            isReadOnly
                            onClose={() => setExpandedCategoryId("")}
                            startDate={startDate}
                            endDate={endDate}
                          />
                        </AccordionAnimation>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </AnimatePresence>
              </Fragment>
            );
          }}
        </TableUI>
      </Async.Success>
    </Async.Root>
  );
};
